import {AnyElement, AnyNode, create, ifjs, StringOrSignal} from "../../fjsc/src/f2.ts";
import {compute, signal, Signal} from "../../fjsc/src/signals.ts";
import {Nav} from "./nav.ts";
import {Route} from "../routing/Route.ts";
import type {NavItem} from "../models/NavItem.ts";
import {Home} from "./home.ts";
import {currency} from "../functions/formatters.ts";
import {About} from "./about.ts";
import {Bagels} from "./bagels.ts";
import {Socials} from "./socials.ts";
import {Menu} from "./menu.ts";
import {Gallery} from "./gallery.ts";

export class Generics {
    static notFound() {
        return Generics.pageFrame(
            true,
            create("h1")
                .text("404")
                .build()
        );
    }

    static iconButton(icon: string, onclick: Function, classes: string[] = []) {
        return create("div")
            .classes("flex", "center-items", "icon-button", ...classes)
            .onclick(onclick)
            .children(
                Generics.icon(icon)
            ).build();
    }

    static nav() {
        const open = signal(false);
        const openClass = compute((o): string => o ? "open" : "_", open);

        return create("nav")
            .classes("flex-v", "relative", "burger-menu-container", openClass)
            .children(
                Generics.iconButton("menu", () => open.value = !open.value, ["burger-menu-button"]),
                ifjs(open, create("div")
                    .classes("burger-menu", "flex-v", "small-gap")
                    .children(
                        ...routes.filter(r => r.showInNav === true)
                            .map(r => {
                                return Nav.navItem(<NavItem>{
                                    text: r.title,
                                    path: r.path,
                                    icon: r.icon,
                                });
                            })
                    ).build())
            ).build();
    }

    static pageFrame(displayBagel: boolean, ...content: (AnyElement | Signal<AnyElement>)[]) {
        return create("div")
            .classes("container", "flex-v")
            .children(
                Generics.nav(),
                ifjs(displayBagel, create("a")
                    .classes("center-self", "header-image")
                    .href("/")
                    .children(
                        Generics.image("/images/bagel.png")
                    ).build()),
                ...content
            ).build();
    }

    static container(layer: number, content: (AnyElement | Signal<AnyElement>)[], extraClasses: string[] = []) {
        return create("div")
            .classes("container", "border", "layer-" + layer, ...extraClasses)
            .children(...content)
            .build();
    }

    static image(src: StringOrSignal, extraClasses: StringOrSignal[] = [], size = "50") {
        return create("img")
            .classes(...extraClasses)
            .height(size)
            .src(src)
            .build();
    }

    static icon(icon: StringOrSignal, classes: StringOrSignal[] = [], onclick: Function = () => {
    }) {
        return create("i")
            .classes("material-symbols-outlined", ...classes)
            .text(icon)
            .onclick(onclick)
            .build();
    }

    static heading(level: number, text: StringOrSignal, classes: StringOrSignal[] = []) {
        return create(`h${level}`)
            .classes(...classes)
            .text(text)
            .build();
    }

    static currency(amount: Signal<number>) {
        const formatted = compute(a => currency(a), amount);

        return create("span")
            .classes("currency")
            .text(formatted)
            .build();
    }

    static paragraph(text: string) {
        return create("p")
            .text(text)
            .build();
    }
}

export const routes: Route[] = [
    {
        path: "/",
        title: "Home",
        aliases: ["home"],
        template: Home.page,
        icon: "home",
    },
    {
        path: "404",
        title: "404",
        aliases: ["error", "not-found"],
        template: Generics.notFound,
    },
    {
        path: "bagels",
        title: "Bagels",
        template: Bagels.page,
        icon: "menu_book",
        showInNav: true,
    },
    {
        path: "about",
        title: "About",
        template: About.page,
        icon: "info",
        showInNav: true,
    },
    {
        path: "socials",
        title: "Socials",
        template: Socials.page,
        icon: "rss_feed",
        showInNav: true,
    },
    {
        path: "menu",
        title: "Menu",
        template: Menu.page,
        icon: "local_dining",
        showInNav: true,
    },
    {
        path: "gallery",
        aliases: ["images"],
        title: "Gallery",
        template: Gallery.page,
        icon: "image",
        showInNav: true,
    },
];

export function vertical(...children: AnyNode[]) {
    return create("div")
        .classes("flex-v")
        .children(children)
        .build();
}

export function verticalPage(...children: AnyNode[]) {
    return create("div")
        .classes("flex-v", "center-items", "noflexwrap", "page")
        .children(children)
        .build();
}

export function horizontal(...children: AnyNode[]) {
    return create("div")
        .classes("flex")
        .children(children)
        .build();
}